import FeatureGates, { type Layer } from '@atlaskit/feature-gate-js-client';

import {
	Cohort,
	type ExperimentOverride,
	type FrontendExperimentProps,
	type GateOverride,
	type ServedExperiment,
	type SideBySideExperiment,
} from '../../../types';

export const getFrontendExperiment = ({
	abTestId,
	onError,
}: FrontendExperimentProps & { onError?: (error: Error) => void }): ServedExperiment => {
	try {
		const cohort: string = FeatureGates.getExperiment(abTestId).get('cohort', Cohort.Default);
		const serveExperiment = cohort === Cohort.Test;
		return { serveExperiment, abTestId, cohort };
	} catch (error) {
		onError?.(error as Error);
	}
	return { serveExperiment: false, abTestId, cohort: Cohort.Default };
};

type CreateCheckGateHandler = (overrides: SideBySideExperiment[] | undefined) => CheckGateHandler;
type CheckGateHandler = (name: string) => boolean;

/**
 * Create feature gate with overrides caller
 * - See https://hello.atlassian.net/wiki/x/AZH-AwE
 *
 * This function is designed to be called instead of:
 * - import { fg } from '@atlaskit/platform-feature-flags'
 * - import { checkGate } from '@atlaskit/feature-gate-js-client';
 *
 * It will make use of overrides from Rovo vs Rovo mode if needed, calling to statsig if not (and if there are any errors)
 *
 * Falls back to a standard statsig call if:
 * - search-experiment-overrides-killswitch
 * - errors
 * - no overrides
 */
export const createFeatureGateHandler: CreateCheckGateHandler = (
	overrides: SideBySideExperiment[] | undefined,
) => {
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
	if (FeatureGates.checkGate('search-experiment-overrides-killswitch')) {
		return defaultCheckGate;
	}

	if (!overrides) {
		return defaultCheckGate;
	}

	try {
		const gates = overrides.filter((overrides) => overrides.type === 'gate') as GateOverride[];

		const getFeatureGate = (name: string) => {
			const overrideFeatureGate = gates.find(
				(override) => 'name' in override && override.name === name,
			);

			if (!overrideFeatureGate) {
				return defaultCheckGate(name);
			}

			// Overrides always resolve to true as using an override is equivelant to turning FF on.
			return true;
		};

		return getFeatureGate;
	} catch (e) {
		console.error(e);
		return defaultCheckGate;
	}
};

type CreateFrontendExperimentHandler = (
	overrides?: SideBySideExperiment[] | undefined,
) => FrontendExperimentHandler;
type FrontendExperimentHandler = ({
	abTestId,
	onError,
}: FrontendExperimentProps) => ServedExperiment;

/**
 * Create frontend experiments with overrides caller
 * - See https://hello.atlassian.net/wiki/x/AZH-AwE
 *
 * This function is designed to be called instead of
 * - import { getFrontendExperiment } from '@atlassian/search-experiment'
 *
 * It will make use of overrides from Rovo vs Rovo mode if needed, calling to statsig if not (and if there are any errors)
 *
 * Falls back to a standard statsig call if:
 * - search-experiment-overrides-killswitch
 * - errors
 * - no overrides
 */
export const createFrontendExperimentHandler: CreateFrontendExperimentHandler = (
	overrides: SideBySideExperiment[] | undefined,
) => {
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
	if (FeatureGates.checkGate('search-experiment-overrides-killswitch')) {
		return getFrontendExperiment;
	}

	if (!overrides) {
		return getFrontendExperiment;
	}

	try {
		const experiments = overrides.filter(
			(overrides) => overrides.type === 'experiment',
		) as ExperimentOverride[];

		const customGetFrontendExperiment = ({
			abTestId,
			onError,
		}: {
			abTestId: string;
			onError?: (error: Error) => void;
		}) => {
			const overrideFrontendExperiment = experiments.find(
				(override) => 'name' in override && override.name === abTestId,
			);

			if (!overrideFrontendExperiment) {
				return getFrontendExperiment({ abTestId, onError });
			}

			return {
				abTestId,
				serveExperiment: true,
				cohort:
					'cohort' in overrideFrontendExperiment ? overrideFrontendExperiment.cohort : Cohort.Test,
			} as ServedExperiment;
		};

		return customGetFrontendExperiment;
	} catch (e) {
		console.error(e);
		return getFrontendExperiment;
	}
};

// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
const defaultCheckGate: CheckGateHandler = (name: string) => FeatureGates.checkGate(name);

type GetLayerOptions = {
	/**
	 * Whether or not to fire the exposure event for the layer. Defaults to true. To log an exposure
	 * event manually at a later time, use {@link FeatureGates.manuallyLogLayerExposure}
	 * (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
	 */
	fireLayerExposure?: boolean;
};

/**
 * This function attempts to obtain the `uiExperiment` within the `layerName`.
 *
 * * If `uiExperimentName` does not match the one obtained in `layerName`, an undefined value is returned.
 * * If `uiExperimentName` matches the one obtained in `layerName`, the experiment is returned via the `Layer` type.
 *
 *
 * Layer exposures can be controlled via the `options.fireLayerExposure` parameter.
 *
 * Usage when fireLayerExposure is true:
 * ```
 * // No exposure is fired yet because we haven't accessed a parameter within the layer
 * const experiment = getMutuallyExclusiveExperiment('example-experiment-name', { fireLayerExposure: true })
 * // An exposure is fired here because the parameter was accessed
 * // and when we obtained the experiment, we had options = { fireLayerExposure: true }
 * experiment.get('cohort')
 * ```
 *
 * Usage when fireLayerExposure is false:
 * ```
 * // No exposure will ever be fired because we have options = { fireLayerExposure: false }
 * const experiment = getMutuallyExclusiveExperiment('example-experiment-name', { fireLayerExposure: false })
 * // No exposure fired here either
 * experiment.get('cohort')
 * ```
 */
export const getMutuallyExclusiveUIExperiment = (
	uiExperimentName: string,
	layerName: string,
	options: GetLayerOptions = { fireLayerExposure: true },
): Layer | undefined => {
	const layer = FeatureGates.getLayer(layerName, { fireLayerExposure: false });
	const frontendExperimentName = layer.get<string>('frontendExperimentName', '');
	if (frontendExperimentName === uiExperimentName) {
		return FeatureGates.getLayer(layerName, options);
	}
	return undefined;
};
